import { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "tyrespro_cookie_consent_v1";

/**
 * Lightweight UK GDPR cookie consent banner.
 * Stores user choice in localStorage. Dispatches a window event when accepted
 * so Google Analytics / Ads scripts can lazy-load (see index.html gating).
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return !localStorage.getItem(STORAGE_KEY);
    } catch (_) {
      return true;
    }
  });

  useEffect(() => {
    // If the user previously accepted, propagate consent to gtag on mount.
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v === "accepted") fireConsentEvent("granted");
    } catch (_) { /* ignore */ }
  }, []);

  const accept = () => {
    try { localStorage.setItem(STORAGE_KEY, "accepted"); } catch (_) { /* ignore */ }
    fireConsentEvent("granted");
    setVisible(false);
  };

  const reject = () => {
    try { localStorage.setItem(STORAGE_KEY, "rejected"); } catch (_) { /* ignore */ }
    fireConsentEvent("denied");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-label="Cookie consent"
      className="fixed bottom-36 right-3 left-3 sm:left-auto sm:right-6 sm:bottom-6 z-[60] max-w-md"
    >
      <div className="bg-[#0A0A0A] text-white rounded-xl sm:rounded-2xl shadow-2xl border border-white/10 p-3.5 sm:p-6">
        <div className="flex items-start gap-2.5 sm:gap-3">
          <div className="hidden sm:flex w-9 h-9 rounded-lg bg-[#1E63FF]/15 text-[#1E63FF] items-center justify-center shrink-0">
            <Cookie className="w-5 h-5" strokeWidth={1.8} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-semibold text-sm sm:text-base tracking-tight text-white inline-flex items-center gap-2">
              <Cookie className="w-4 h-4 text-[#1E63FF] sm:hidden" strokeWidth={1.8} />
              Cookies on TyresPro
            </h3>
            <p className="mt-1 sm:mt-1.5 text-[12px] sm:text-[13px] leading-snug sm:leading-relaxed text-white/70">
              We use essential cookies, plus optional analytics if you accept.
              See our{" "}
              <a href="/privacy.html" className="underline hover:text-white">Privacy Policy</a>.
            </p>
            <div className="mt-3 sm:mt-4 flex flex-wrap gap-2">
              <button
                data-testid="cookie-accept"
                onClick={accept}
                className="btn-blue h-9 sm:h-10 px-4 sm:px-5 rounded-full text-[13px] sm:text-sm font-semibold"
              >
                Accept all
              </button>
              <button
                data-testid="cookie-reject"
                onClick={reject}
                className="h-9 sm:h-10 px-4 sm:px-5 rounded-full text-[13px] sm:text-sm font-semibold border border-white/20 text-white hover:bg-white/10 transition-colors"
              >
                Reject
              </button>
            </div>
          </div>
          <button
            aria-label="Dismiss"
            onClick={reject}
            className="text-white/50 hover:text-white transition-colors mt-0.5"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function fireConsentEvent(state) {
  // Update Google Consent Mode v2 (if gtag is loaded)
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        ad_storage: state,
        analytics_storage: state,
        ad_user_data: state,
        ad_personalization: state,
      });
    }
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("tp:consent", { detail: state }));
    }
  } catch (_) { /* noop */ }
}
